<template>
  <section class="bg-[#f9fbfd] px-12 pt-6 flex flex-col gap-4">
    <!-- Breadcrumb -->
    <Breadcrumb
      :stack="breadcrumbStack"
      @item-click="handleBreadcrumbClick"
    />

    <div class="flex items-start gap-6">
      <div class="h-14 w-14 min-h-14 min-w-14 flex-shrink-0 rounded-full bg-[#f2f2f2] flex items-center justify-center text-lg text-[#979797] font-medium">
        {{ derivedInitials }}
      </div>
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-3 flex-wrap">
          <h2 class="text-2xl font-medium text-[#333] leading-7">{{ derivedName }}</h2>
            <div class="relative group inline-flex">
            <button
              class="bg-[#E4F3F5] text-[#004879] text-sm font-medium px-4 py-1 rounded-full hover:bg-[#C5E3E7] transition flex gap-x-2 items-center"
              type="button"
              @click="showSwitchModal = true"
            >
              <img src="@/assets/encounterNotes/user.svg" alt="user" />
              SWITCH PATIENT
              <img src="@/assets/encounterNotes/switch.svg" alt="switch" />
            </button>
            <div class="tooltip group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-[-50%]">
              Switch to another patient
            </div>
          </div>
        </div>
        <div class="flex flex-wrap gap-x-6 text-sm text-[#333] leading-6">
          <span>Bio Gender: {{ bioGender }}</span>
          <span class="text-[#c4c4c4]">|</span>
          <span>DOB: {{ dob }}</span>
          <span class="text-[#c4c4c4]">|</span>
          <span>Current Visit: {{ currentVisit }}</span>
          <span class="text-[#c4c4c4]">|</span>
          <span>Date: {{ visitDate }}</span>
        </div>
      </div>
    </div>

    <PastNotesList 
      :notes="pastNotes" 
      :patient-id="route.params.patientId"
      @before-navigate="$emit('beforeNavigate', $event)"
    />

    <SwitchPatientModal
      v-model="showSwitchModal"
      @confirm="handlePatientSwitch"
    />
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SwitchPatientModal from './SwitchPatientModal.vue';
import PastNotesList from './PastNotesList.vue';

const props = defineProps({
  firstName: { type: String, default: '' },
  lastName: { type: String, default: '' },
  bioGender: { type: String, default: '' },
  dob: { type: String, default: '' },
  currentVisit: { type: String, default: '' },
  visitDate: { type: String, default: '' },
  pastNotes: {
    type: Array,
    default: () => []
  }
});

defineEmits(["beforeNavigate"]);

const derivedName = computed(() => `${props.firstName || ''} ${props.lastName || ''}`.trim() || 'Patient');

const derivedInitials = computed(() => {
  const first = props.firstName?.trim() || '';
  const last = props.lastName?.trim() || '';
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  return 'NA';
});

const route = useRoute();
const router = useRouter();
const showSwitchModal = ref(false);

// Library Breadcrumb stack: each entry needs a stable `id` and a display `name`.
// The last item is treated as the current page and rendered non-clickable by
// the component, so we only need to handle navigation for prior items.
const breadcrumbStack = [
  { id: 'encounter-notes', name: 'Encounter Notes Center' },
  { id: 'patient', name: 'Patient' },
];

const handleBreadcrumbClick = (item) => {
  if (item.id === 'encounter-notes') {
    router.push('/encounter-notes');
  }
};

const handlePatientSwitch = (patient) => {
  showSwitchModal.value = false;
  
  if (!patient || !patient.patient_id) {
    return;
  }
  
  const currentPatientId = route.params.patientId ? String(route.params.patientId) : null;
  const selectedPatientId = String(patient.patient_id);
  
  // If selected patient is same as current, do nothing
  if (currentPatientId === selectedPatientId) {
    return;
  }
  
  // Navigate to the new patient's encounter note page
  router.push(`/encounter-notes/${selectedPatientId}/new`);
};
</script>

<style scoped>
.tooltip {
  position: absolute;
  left: 50%;
  top: calc(100% + 10px);
  transform: translate(-50%, -8px);
  background: #545454;
  color: #fff;
  font-size: 12px;
  line-height: 16px;
  padding: 6px 10px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s ease, transform 0.15s ease;
  z-index: 10;
  pointer-events: none;
}

.tooltip::before {
  content: '';
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  border-width: 12px;
  border-style: solid;
  border-color: transparent transparent #545454 transparent;
}
</style>

